import React, { useState, useEffect } from 'react';
import { 
  Users, AlertTriangle, TrendingUp, CheckCircle, Clock, 
  Briefcase, Calendar, ChevronDown, ChevronUp, UserX, Filter, Loader2
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = '/api';

/**
 * Team Workload View - Macro-level resource planning
 * Shows workload across all cases for entire team
 */
const TeamWorkloadView = () => {
  const [loading, setLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);
  const [allCases, setAllCases] = useState([]);
  const [expandedMember, setExpandedMember] = useState(null);
  const [viewMode, setViewMode] = useState('workload'); // workload, no-lead, high-risk
  const [weekView, setWeekView] = useState(0); // 0 = this week, 1 = next week, etc.

  useEffect(() => {
    fetchTeamWorkload();
  }, []);

  const fetchTeamWorkload = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Fetch all team data
      const [usersRes, casesRes, tasksRes] = await Promise.all([
        axios.get(`${API}/admin/users`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/cases/my-cases/unified?filter=all`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/tasks?status=TODO,IN_PROGRESS`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      const users = usersRes.data.users || [];
      const cases = casesRes.data.cases || [];
      const tasks = tasksRes.data.tasks || [];

      // Calculate workload per member
      const membersWithWorkload = users.map(user => {
        // Cases where user is lead
        const ledCases = cases.filter(c => 
          c.lawyer_id === user.id || c.assigned_to === user.id
        );

        // Tasks assigned to user
        const assignedTasks = tasks.filter(t => t.assignee_user_id === user.id);

        // Calculate total hours
        const totalHours = assignedTasks.reduce((sum, t) => 
          sum + (t.estimated_hours || 0), 0
        );

        // Get upcoming deadlines
        const upcomingDeadlines = assignedTasks
          .filter((t: any) => t.due_date)
          .sort((a: any, b: any) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime())
          .slice(0, 3);

        return {
          ...user,
          led_cases_count: ledCases.length,
          led_cases: ledCases,
          assigned_tasks_count: assignedTasks.length,
          assigned_tasks: assignedTasks,
          total_hours: totalHours,
          upcoming_deadlines: upcomingDeadlines,
          workload_status: getWorkloadStatus(totalHours)
        };
      });

      setTeamMembers(membersWithWorkload);
      setAllCases(cases);
    } catch (error) {
      console.error('Failed to fetch team workload:', error);
      toast.error('Kunne ikke laste team workload');
    } finally {
      setLoading(false);
    }
  };

  const getWorkloadStatus = (hours) => {
    if (hours > 40) return { status: 'overloaded', color: 'bg-red-500', label: 'Overbelastet', textColor: 'text-red-900' };
    if (hours > 20) return { status: 'busy', color: 'bg-orange-500', label: 'Høy', textColor: 'text-orange-900' };
    if (hours > 10) return { status: 'moderate', color: 'bg-yellow-500', label: 'Moderat', textColor: 'text-yellow-900' };
    return { status: 'available', color: 'bg-green-500', label: 'Ledig', textColor: 'text-green-900' };
  };

  const casesWithoutLead = allCases.filter(c => 
    !c.lawyer_id && !c.assigned_to
  );

  const highRiskCases = allCases.filter(c => {
    // Cases with deadlines within 3 days and high workload
    // This is simplified - in real app, check actual deadlines
    const status = c.status;
    return status === 'IN_PROGRESS' && c.priority === 'HIGH';
  });

  const totalTeamHours = teamMembers.reduce((sum, m) => sum + m.total_hours, 0);
  const avgHoursPerMember = teamMembers.length > 0 ? totalTeamHours / teamMembers.length : 0;
  const overloadedMembers = teamMembers.filter(m => m.workload_status.status === 'overloaded');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Team Workload</h2>
          <p className="text-sm text-gray-600 mt-1">
            Oversikt over kapasitet og ressurser på tvers av alle saker
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setViewMode('workload')}
            variant={viewMode === 'workload' ? 'default' : 'outline'}
            size="sm"
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            Workload
          </Button>
          <Button
            onClick={() => setViewMode('no-lead')}
            variant={viewMode === 'no-lead' ? 'default' : 'outline'}
            size="sm"
          >
            <UserX className="w-4 h-4 mr-2" />
            Uten lead
          </Button>
          <Button
            onClick={() => setViewMode('high-risk')}
            variant={viewMode === 'high-risk' ? 'default' : 'outline'}
            size="sm"
          >
            <AlertTriangle className="w-4 h-4 mr-2" />
            Høy risiko
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5 text-gray-600" />
            <span className="text-sm font-medium text-gray-600">Team</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{teamMembers.length}</p>
          <p className="text-xs text-gray-500 mt-1">medlemmer</p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-5 h-5 text-blue-600" />
            <span className="text-sm font-medium text-blue-900">Total workload</span>
          </div>
          <p className="text-2xl font-bold text-blue-900">{totalTeamHours.toFixed(1)}t</p>
          <p className="text-xs text-blue-700 mt-1">Snitt: {avgHoursPerMember.toFixed(1)}t per person</p>
        </div>

        <div className={`border rounded-lg p-4 ${
          overloadedMembers.length > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
        }`}>
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className={`w-5 h-5 ${overloadedMembers.length > 0 ? 'text-red-600' : 'text-green-600'}`} />
            <span className={`text-sm font-medium ${overloadedMembers.length > 0 ? 'text-red-900' : 'text-green-900'}`}>
              Overbelastet
            </span>
          </div>
          <p className={`text-2xl font-bold ${overloadedMembers.length > 0 ? 'text-red-900' : 'text-green-900'}`}>
            {overloadedMembers.length}
          </p>
          <p className={`text-xs mt-1 ${overloadedMembers.length > 0 ? 'text-red-700' : 'text-green-700'}`}>
            {overloadedMembers.length > 0 ? 'personer' : 'Ingen overbelastet'}
          </p>
        </div>

        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <UserX className="w-5 h-5 text-orange-600" />
            <span className="text-sm font-medium text-orange-900">Uten lead</span>
          </div>
          <p className="text-2xl font-bold text-orange-900">{casesWithoutLead.length}</p>
          <p className="text-xs text-orange-700 mt-1">saker trenger tildeling</p>
        </div>
      </div>

      {/* Content based on view mode */}
      {viewMode === 'workload' && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Team kapasitet</h3>
          <div className="space-y-4">
            {teamMembers
              .sort((a, b) => b.total_hours - a.total_hours) // Sort by workload
              .map(member => {
                const isExpanded = expandedMember === member.id;
                const status = member.workload_status;

                return (
                  <div key={member.id} className="border border-gray-200 rounded-lg p-4">
                    <div 
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => setExpandedMember(isExpanded ? null : member.id)}
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                          <Users className="w-6 h-6 text-gray-600" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <p className="font-semibold text-gray-900">{member.name}</p>
                            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                              {member.role}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">{member.email}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-gray-900">{member.total_hours.toFixed(1)}t</p>
                          <p className="text-xs text-gray-500">
                            {member.assigned_tasks_count} oppgaver • {member.led_cases_count} saker
                          </p>
                        </div>
                        <div className={`w-3 h-3 rounded-full ${status.color}`} title={status.label} />
                        {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                      </div>
                    </div>

                    {/* Workload Bar */}
                    <div className="mt-3">
                      <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${status.color} transition-all`}
                          style={{ width: `${Math.min((member.total_hours / 40) * 100, 100)}%` }}
                        />
                      </div>
                      <div className="flex justify-between mt-1">
                        <p className="text-xs text-gray-500">
                          {((member.total_hours / 40) * 100).toFixed(0)}% av 40t/uke
                        </p>
                        <p className={`text-xs font-medium ${status.textColor}`}>
                          {status.label}
                        </p>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        {/* Led Cases */}
                        {member.led_cases.length > 0 && (
                          <div className="mb-4">
                            <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                              <Briefcase className="w-4 h-4" />
                              Saker som lead ({member.led_cases_count})
                            </h4>
                            <div className="space-y-2">
                              {member.led_cases.slice(0, 5).map(c => (
                                <div key={c.id} className="flex items-center justify-between text-sm p-2 bg-gray-50 rounded">
                                  <span className="text-gray-700">{c.topic}</span>
                                  <span className="text-xs text-gray-500">{c.category}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Assigned Tasks */}
                        {member.assigned_tasks.length > 0 && (
                          <div className="mb-4">
                            <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                              <CheckCircle className="w-4 h-4" />
                              Tildelte oppgaver ({member.assigned_tasks_count})
                            </h4>
                            <div className="space-y-2">
                              {member.assigned_tasks.slice(0, 5).map(task => (
                                <div key={task.id} className="flex items-center justify-between text-sm p-2 bg-gray-50 rounded">
                                  <span className="text-gray-700">{task.title}</span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-gray-500">{task.estimated_hours}t</span>
                                    {task.due_date && (
                                      <span className="text-xs text-gray-400">
                                        {new Date(task.due_date).toLocaleDateString('no-NO')}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Upcoming Deadlines */}
                        {member.upcoming_deadlines.length > 0 && (
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              Kommende frister
                            </h4>
                            <div className="space-y-1">
                              {member.upcoming_deadlines.map(task => (
                                <div key={task.id} className="flex items-center justify-between text-xs p-2 bg-gray-50 rounded">
                                  <span className="text-gray-700">{task.title}</span>
                                  <span className="text-gray-500">
                                    {new Date(task.due_date).toLocaleDateString('no-NO')}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {viewMode === 'no-lead' && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <UserX className="w-5 h-5 text-orange-600" />
            Saker uten lead ({casesWithoutLead.length})
          </h3>
          {casesWithoutLead.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="text-gray-600">Alle saker har en lead! 🎉</p>
            </div>
          ) : (
            <div className="space-y-3">
              {casesWithoutLead.map(c => (
                <div key={c.id} className="flex items-center justify-between p-4 border border-orange-200 bg-orange-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{c.topic}</p>
                    <p className="text-sm text-gray-600">{c.category}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Opprettet: {new Date(c.created_at).toLocaleDateString('no-NO')}</p>
                    <Button size="sm" className="mt-2">
                      Tildel lead
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {viewMode === 'high-risk' && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            Høy risiko saker ({highRiskCases.length})
          </h3>
          {highRiskCases.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="text-gray-600">Ingen høy risiko saker akkurat nå</p>
            </div>
          ) : (
            <div className="space-y-3">
              {highRiskCases.map(c => (
                <div key={c.id} className="flex items-center justify-between p-4 border border-red-200 bg-red-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{c.topic}</p>
                    <p className="text-sm text-gray-600">{c.category}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded">
                        {c.priority} prioritet
                      </span>
                      <span className="text-xs text-gray-500">
                        {c.status}
                      </span>
                    </div>
                  </div>
                  <Button size="sm">
                    Se detaljer
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TeamWorkloadView;
